/**
 * The builder's address bar: `/plans/$planId?phase=`.
 *
 * Execution ADR §4 makes the builder **one route and one document**, with selection carried in
 * search parameters rather than in nested routes. Two things fall out of that and both are the
 * point: the Back button collapses a selection instead of unloading the plan, and a trainer can
 * send a colleague a link to the exact thing they are looking at. localStorage holds defaults only
 * — density, rail collapsed — never selection.
 *
 ── All three parameters ship as of PR-B2C ──────────────────────────────────────────────────
 * The ADR's full address is `?phase=&day=<slotToken>&dock=panel|full`, and it is now complete.
 * Adding the two was a key in `PARSERS` plus a field on the interface, exactly as PR-A1's shape
 * promised — the validator, the hook and every existing caller are untouched.
 *
 * That is also why the hook exposes `update` (a partial merge) rather than a setter per parameter:
 * three setters would each have to remember to preserve the other two, which is exactly how a dock
 * mode gets dropped by a phase change.
 *
 * ── The URL OWNS the dock, and that is what makes Back work ──────────────────────────────────
 * Opening a day PUSHES a history entry while every other change replaces one. So the browser's Back
 * collapses the dock and leaves the trainer on the plan they were reading, rather than unloading it
 * — which is the single behaviour execution ADR §4 gives as the reason selection lives in the
 * address at all. Nothing else in the builder pushes, so Back never walks backwards through a
 * trainer's own phase browsing.
 *
 * ── The number in the URL is 1-based; the index in the code is 0-based ───────────────────────
 * `?phase=2` is the trainer's second phase, because a URL is something a person reads and types.
 * Everything under it counts from zero. The conversion happens here, once, at the boundary — and
 * it is the reason `phaseIndex` and `phaseNumber` are never the same word in this slice.
 */

import { useNavigate, useSearch } from '@tanstack/react-router';

import { TEXT_LIMITS } from '~/entities/plan-draft';

/**
 * Which host the day form is rendered in. Declared in `model/dockDefault`, which is the module that
 * decides what a stored `panel` / `full` MEANS — `model/` may not import from `lib/`, so the type
 * lives there and this file re-exports it for the callers that reach the dock through the address.
 */
import type { DayDockMode } from '../model/dockDefault';

export type { DayDockMode };

/** The builder's search parameters. */
export interface PlanBuilderSearch {
  /**
   * The phase the canvas is showing, 1-based. Absent means the first phase, which is why it is
   * optional rather than defaulted to 1: an explicit `?phase=1` on every URL would be noise in a
   * link the trainer is meant to share.
   */
  readonly phase?: number;
  /**
   * The day slot token the dock is open on. A TOKEN rather than an index, because a token is the
   * app's durable exercise-and-day address: a link survives a day being reordered, and a day that
   * has since been deleted resolves to nothing rather than to whichever day took its place.
   */
  readonly day?: string;
  /** Absent means the dock is closed. `panel` is the 604px dock; `full` is the whole canvas. */
  readonly dock?: DayDockMode;
  /**
   * The Buddy panel beside the week is open (ai-plan-builder, board S). In the address, so a reload
   * and Back keep it where the trainer left it, independently of the day dock.
   */
  readonly asystent?: true;
}

/**
 * One parser per key, so a new parameter is one entry.
 *
 * Each returns `undefined` for anything it cannot read, and `undefined` means "not in the URL" —
 * which is how a stale or hand-edited link degrades to the default instead of to an error page.
 * A bookmark from before a phase was deleted is a normal thing to receive.
 */
const PARSERS = {
  phase: (raw: unknown): number | undefined => {
    const value = typeof raw === 'string' ? Number(raw) : typeof raw === 'number' ? raw : Number.NaN;
    // Integers only, and at least 1. `weekProjection` clamps the upper end against the plan that
    // actually loaded, which this validator cannot see — it runs before the read.
    return Number.isInteger(value) && value >= 1 ? value : undefined;
  },
  day: (raw: unknown): string | undefined => {
    if (typeof raw !== 'string') return undefined;
    const token = raw.trim();
    // Bounded at the wire's own ceiling for the column. A longer value cannot name any day this app
    // could load, and letting an arbitrary string ride in the address is a needless surface.
    return token.length > 0 && token.length <= TEXT_LIMITS.slotToken.max ? token : undefined;
  },
  dock: (raw: unknown): DayDockMode | undefined =>
    raw === 'panel' || raw === 'full' ? raw : undefined,
  asystent: (raw: unknown): true | undefined => (raw === true || raw === 'true' || raw === 1 || raw === '1' ? true : undefined),
} satisfies Record<keyof PlanBuilderSearch, (raw: unknown) => unknown>;

/**
 * The route's `validateSearch`.
 *
 * It lives in the slice rather than in `app/routes/`, because which parameters the builder
 * understands is the builder's decision — a route file is a wiring declaration, and anything a
 * second route could want does not live there.
 */
export function validatePlanBuilderSearch(raw: Record<string, unknown>): PlanBuilderSearch {
  const phase = PARSERS.phase(raw.phase);
  const day = PARSERS.day(raw.day);
  const dock = PARSERS.dock(raw.dock);
  const asystent = PARSERS.asystent(raw.asystent);
  // Every key is OMITTED when it parses to nothing, rather than set to `undefined`: TanStack Router
  // serialises the object back into the URL, and an explicit `undefined` leaves `?phase=` behind.
  //
  // A `dock` with no `day` is dropped WHOLE. The two are one selection — a dock mode names how to
  // render a day, so a mode with nothing to render is a hand-edited or half-copied link, and
  // honouring it would open an empty panel with no way to say what it is for.
  return {
    ...(phase === undefined ? {} : { phase }),
    ...(day === undefined || dock === undefined ? {} : { day, dock }),
    ...(asystent === undefined ? {} : { asystent }),
  };
}

export interface PlanBuilderSearchState {
  /** Zero-based, for `weekProjection`. Clamped there against the plan that actually loaded. */
  readonly phaseIndex: number;
  /** The day the dock is open on, or `null` when it is closed. */
  readonly daySlotToken: string | null;
  /** How the open day is rendered, or `null` when the dock is closed. */
  readonly dock: DayDockMode | null;
  /** The Buddy panel is open beside the week. */
  readonly assistantOpen: boolean;
  /**
   * Merge a change into the address.
   *
   * REPLACES by default: paging through phase tabs is looking around one document, not navigating
   * between documents, and a history entry per tab would make Back walk backwards through the
   * trainer's own browsing instead of leaving the plan.
   *
   * `push` is for the one change that must be undoable with the Back button — opening the dock. See
   * the header.
   */
  readonly update: (next: Partial<PlanBuilderSearch>, options?: { readonly push?: boolean }) => void;
}

export function usePlanBuilderSearch(): PlanBuilderSearchState {
  // ── Route-agnostic, because the builder now has TWO addresses ──────────────────────────────
  // `/plans/$planId` opens a stored plan and `/plans/nowy` opens a draft that exists only in this
  // tab. Both mount the same screen and both register `validatePlanBuilderSearch`, so binding this
  // hook to one route id would make the other one throw.
  //
  // `strict: false` is the router's own way to read the address without naming a route, and the
  // shape is re-validated through the slice's OWN validator rather than cast: that is not ceremony,
  // it is what makes the non-strict read as safe as the strict one was, and it keeps a single
  // definition of which parameters the builder understands. `useNavigate()` with no `from` resolves
  // relative to the current match, so a search-only update stays on whichever of the two it is.
  const search = validatePlanBuilderSearch(useSearch({ strict: false }));
  const navigate = useNavigate();

  return {
    phaseIndex: (search.phase ?? 1) - 1,
    daySlotToken: search.day ?? null,
    dock: search.dock ?? null,
    assistantOpen: search.asystent === true,
    update: (next, options) => {
      // Merged from the search this hook ALREADY read rather than from the router's updater
      // callback. That is not a style choice: with no `from`, `useNavigate` types the updater's
      // return as `never`, because it cannot know which route's schema the reducer is producing. The
      // value is identical — `search` above is this render's address, validated — and the merge stays
      // in one place instead of being split between here and a callback.
      const merged = { ...search, ...next };
      // `undefined` is how a caller says "take this key out", and TanStack Router serialises an
      // explicit `undefined` as a bare `?day=`. Stripping them here keeps a closed dock's URL
      // identical to one that was never opened, which is what makes Back reach a clean address.
      const cleaned = Object.fromEntries(
        Object.entries(merged).filter(([, value]) => value !== undefined),
      );
      void navigate({
        // Relative to whichever of the two builder routes is mounted, so the same hook serves both
        // without either naming the other.
        to: '.',
        search: cleaned,
        replace: options?.push !== true,
      });
    },
  };
}
